import { ref } from 'vue'
import { defineStore } from 'pinia'
import {
  File,
  Annotation,
  NameValue,
  TaxonomySuggestion,
  Statistics,
  ML_Model,
  User
} from '@/misc/types'
import Api from '@/api'

export const useAdminStore = defineStore('admin', () => {
  const files = ref<File[]>([])
  const feedbacks = ref<Annotation[]>([])
  const annotations = ref<Annotation[]>([])
  const feedbackTotal = ref<number>(0)
  const annotationTotal = ref<number>(0)
  const annotationResolvedCounts = ref<Record<string, number>>({})
  const fileTotal = ref<number>(0)
  const taxonomySuggestion = ref<TaxonomySuggestion[]>([])
  const taxonomy = ref<NameValue<string>[]>([])
  const projects = ref<NameValue<string>[]>([])
  const origins = ref<NameValue<string>[]>([])
  const statistics = ref<Statistics>()
  const models = ref<ML_Model[]>([])
  const users = ref<User[]>([])
  // Global taxonomy + grouping
  const globalTaxonomy = ref<
    {
      id: number
      name: string
      merged_from_count?: number
      merged_from_names?: string[]
    }[]
  >([])
  const globalTaxonomyStats = ref<{
    by_final: { global_id: number; name: string; count: number; project_names: string[] }[]
    by_label: {
      label_id: number
      label_name: string
      final_id: number
      final_name: string
      is_merged: boolean
      count: number
      project_names: string[]
    }[]
  }>({ by_final: [], by_label: [] })
  const taxonomyGroups = ref<
    {
      id: number
      name: string
      description?: string
      label_ids: number[]
      parent_id?: number | null
      child_ids?: number[]
    }[]
  >([])

  const setAdminStatistics = (payload: Statistics) => {
    statistics.value = payload
  }

  const setTaxonomy = (payload: NameValue<string>[]) => {
    taxonomy.value = payload
  }

  const setProjects = (payload: any[]) => {
    projects.value = payload.map((data) => new NameValue(data['project_name'], data['project_id']))
  }

  const setTaxonomySuggestion = (payload: any[]) => {
    taxonomySuggestion.value = payload.map(
      (data: any) =>
        new TaxonomySuggestion(
          data.taxonomy_suggestion_id,
          new Date(data.taxonomy_suggestion_timestamp),
          data.taxonomy_suggestion_created,
          data.taxonomy_suggestion_resolved,
          data.taxonomy_suggestion_name,
          data.taxonomy_suggestion_project_id
        )
    )
  }

  const setGlobalTaxonomy = (payload: any[]) => {
    globalTaxonomy.value = payload.map((l: any) => ({
      id: l.id,
      name: l.name,
      merged_from_count: l.merged_from_count || 0,
      merged_from_names: l.merged_from_names || []
    }))
  }

  const setGlobalTaxonomyStats = (payload: any) => {
    const byFinal = (payload?.by_final || []).map((r: any) => ({
      global_id: r.global_id,
      name: r.name,
      count: r.count,
      project_names: r.project_names || []
    }))

    const byLabel = (payload?.by_label || []).map((r: any) => ({
      label_id: r.label_id,
      label_name: r.label_name,
      final_id: r.final_id,
      final_name: r.final_name,
      is_merged: !!r.is_merged,
      count: r.count,
      project_names: r.project_names || []
    }))

    globalTaxonomyStats.value = { by_final: byFinal, by_label: byLabel }
  }

  const setTaxonomyGroups = (payload: any[]) => {
    taxonomyGroups.value = payload.map((g: any) => ({
      id: g.id,
      name: g.name,
      description: g.description,
      label_ids: g.label_ids || [],
      parent_id: g.parent_id ?? null,
      child_ids: g.child_ids || []
    }))
  }
  const setModels = (payload: any[]) => {
    models.value = payload.map(
      (data: any) =>
        new ML_Model(
          data.ml_model_id,
          new Date(data.ml_model_timestamp),
          data.ml_model_created,
          data.ml_model_name,
          data.ml_model_project_id
        )
    )
  }

  const setUsers = (payload: any[]) => {
    users.value = payload.map(
      (data: any) =>
        new User(
          data.user_account_oid,
          data.user_account_name,
          data.user_account_role,
          data.user_account_username,
          data.user_project_project_ids,
          false,
          data.user_account_managed_usernames || [],
          (data.user_account_managed_project_ids || []).map((pid: any) => Number(pid)),
          false,
          null,
          null,
          null,
          null
        )
    )
  }

  const setFeedback = (payload: any, append = false) => {
    const items = Array.isArray(payload) ? payload : (payload?.items ?? [])

    const total = payload?.total
    feedbackTotal.value =
      typeof total === 'number' ? total : append ? feedbackTotal.value : items.length

    const mapped = items.map((data: any) => {
      const file = new File(
        data.file_id,
        new Date(data.file_timestamp),
        new Date(data.file_created),
        data.file_uuid,
        data.file_name,
        data.file_ml_model_id,
        data.file_project_id
      )

      return new Annotation(
        data.annotation_id,
        new Date(data.annotation_timestamp),
        new Date(data.annotation_created),
        'feedback',
        data.annotation_resolved,
        data.annotation_label ?? null,
        data.annotation_score ?? null,
        data.annotation_trim_start ?? null,
        data.annotation_trim_end ?? null,
        data.annotation_comment ?? null,
        data.annotation_trust_level ?? null,
        file
      )
    })

    feedbacks.value = append ? feedbacks.value.concat(mapped) : mapped
  }

  const setAnnotation = (payload: any, append = false) => {
    const items = Array.isArray(payload) ? payload : (payload?.items ?? [])

    const total = payload?.total
    annotationTotal.value =
      typeof total === 'number' ? total : append ? annotationTotal.value : items.length

    const mapped = items.map((data: any) => {
      const file = new File(
        data.file_id,
        new Date(data.file_timestamp),
        new Date(data.file_created),
        data.file_uuid,
        data.file_name,
        data.file_ml_model_id,
        data.file_project_id
      )

      return new Annotation(
        data.annotation_id,
        new Date(data.annotation_timestamp),
        new Date(data.annotation_created),
        data.annotation_kind ?? 'prediction',
        data.annotation_resolved,
        data.annotation_label ?? null,
        data.annotation_score ?? null,
        data.annotation_trim_start ?? null,
        data.annotation_trim_end ?? null,
        data.annotation_comment ?? null,
        data.annotation_trust_level ?? null,
        file
      )
    })

    annotations.value = append ? annotations.value.concat(mapped) : mapped
  }

  const setFiles = (payload: any, append = false) => {
    const items = Array.isArray(payload) ? payload : (payload?.items ?? [])

    const total = payload?.total
    fileTotal.value = typeof total === 'number' ? total : append ? fileTotal.value : items.length

    const existingIds = append ? new Set(files.value.map((f) => f.id)) : new Set()

    const mapped = items
      .filter((data: any) => !existingIds.has(data.file_id))
      .map(
        (data: any) =>
          new File(
            data.file_id,
            new Date(data.file_timestamp),
            new Date(data.file_created),
            data.file_uuid,
            data.file_name,
            data.file_ml_model_id,
            data.file_project_id
          )
      )

    files.value = append ? files.value.concat(mapped) : mapped
  }

  const setAnnotationResolvedCounts = (payload: Record<string, number> = {}) => {
    annotationResolvedCounts.value = payload || {}
  }

  const getFilePath = (file: File) => `${Api.API_URL}/admin/wav/${file.projectId}/${file.name}`

  const getFeedbackInfoText = (id: number) => {
    const feedback = feedbacks.value.find((f) => f.id === id)
    if (!feedback) return ''
    return `Project id: ${feedback.file.projectId}
    Trust level: ${feedback.trustLevel} 
    File created: ${feedback.file.created.toLocaleDateString()}
    Feedback created: ${feedback.created.toLocaleDateString()}`
  }

  const getFileInfoText = (id: number) => {
    const file = files.value.find((f: File) => f.id === id)
    if (!file) return ''
    return `Project id: ${file.projectId}
    Created: ${file.created.toLocaleDateString()} 
    `
  }

  const setOrigin = (payload: string[]) => {
    origins.value = payload.map((data) => new NameValue(data, data))
  }

  return {
    files,
    feedbacks,
    taxonomySuggestion,
    taxonomy,
    projects,
    annotations,
    statistics,
    models,
    users,
    origins,
    globalTaxonomy,
    globalTaxonomyStats,
    taxonomyGroups,
    setAdminStatistics,
    setTaxonomy,
    setTaxonomySuggestion,
    setProjects,
    setModels,
    setUsers,
    setFeedback,
    setAnnotation,
    setFiles,
    setAnnotationResolvedCounts,
    getFilePath,
    getFeedbackInfoText,
    getFileInfoText,
    setOrigin,
    setGlobalTaxonomy,
    setGlobalTaxonomyStats,
    setTaxonomyGroups,
    annotationTotal,
    feedbackTotal,
    fileTotal,
    annotationResolvedCounts
  }
})
